// Modules
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/store';
import { clearAuth, selectIsAuthenticated } from 'features/auth/authSlice';

// Utils
import { hasVersionChanged, getAppVersion, getStoredVersion } from 'utils/version';

export const VERSION_UPDATE_KEY = 'nimax_version_update';

/**
 * Hook that checks if the app version has changed since the last login.
 * If the version changed and user is authenticated, it forces a logout
 * to ensure the user gets fresh data and state after an app update.
 *
 * This hook should be called once at the app root level (e.g., MainLayout).
 */
const useVersionCheck = (): void => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const hasChecked = useRef(false);

  useEffect(() => {
    // Only check once per mount and only if user is authenticated
    if (hasChecked.current || !isAuthenticated) {
      return;
    }

    hasChecked.current = true;

    if (hasVersionChanged()) {
      const storedVersion = getStoredVersion();
      const currentVersion = getAppVersion();

      console.info(
        `[Version Check] App version changed from "${storedVersion}" to "${currentVersion}". Forcing logout.`,
      );

      // Set flag to show version update toast on login page
      localStorage.setItem(VERSION_UPDATE_KEY, 'true');

      // Clear auth state (this also clears the stored version)
      dispatch(clearAuth());

      // Redirect to login
      navigate('/login', { replace: true });
    }
  }, [dispatch, navigate, isAuthenticated]);
};

export default useVersionCheck;
